export enum WorkflowDefinitionValidationSeverityType {
  ERROR = 'ERROR',
  WARNING = 'WARNING',
}

export enum WorkflowDefinitionValidationRule {
  // GRAPH / TOPOLOGY
  GRAPH_EXACTLY_ONE_START = 'GRAPH_EXACTLY_ONE_START',
  GRAPH_EXACTLY_ONE_END = 'GRAPH_EXACTLY_ONE_END',

  START_STEP_NO_INCOMING = 'START_STEP_NO_INCOMING',

  END_STEP_SINGLE_INCOMING = 'END_STEP_SINGLE_INCOMING',
  END_STEP_NO_OUTGOING = 'END_STEP_NO_OUTGOING',

  TRANSITIONS_UNIQUE = 'TRANSITIONS_UNIQUE', // no duplicate (from,to)
  NO_SELF_LOOPS = 'NO_SELF_LOOPS', // no (from === to)
  NO_ISOLATED_STEPS = 'NO_ISOLATED_STEPS', // 0 hanging steps

  ONLY_SPLIT_GATEWAY_CAN_FAN_OUT = 'ONLY_SPLIT_GATEWAY_CAN_FAN_OUT', // only split gateways (parallel, exclusive, inclusive) may have >1 outgoing

  JOIN_GATEWAY_MANY_IN_SINGLE_OUT = 'JOIN_GATEWAY_MANY_IN_SINGLE_OUT', // join: >=2 incoming, exactly 1 outgoing

  CONDITIONAL_SPLIT_SINGLE_DEFAULT = 'CONDITIONAL_SPLIT_SINGLE_DEFAULT', // exactly 1 default outgoing
  EXCLUSIVE_SPLIT_TWO_OUTGOING = 'EXCLUSIVE_SPLIT_TWO_OUTGOING', // exactly 2 outgoing (1 default, 1 conditional)

  ONLY_EXCLUSIVE_INCLUSIVE_STEP_HAVE_CONDITION = 'ONLY_EXCLUSIVE_INCLUSIVE_STEP_HAVE_CONDITION', // except the exclusive and inclusive split steps: outgoing transitions have no conditions

  MISSING_BATCH_ITERATOR = 'MISSING_BATCH_ITERATOR', // batch workflow should have an iterator.

  PARALLEL_SPLIT_REQUIRES_JOIN = 'PARALLEL_SPLIT_REQUIRES_JOIN', // every parallel split must have a reachable parallel join downstream
  PARALLEL_JOIN_REQUIRES_SPLIT = 'PARALLEL_JOIN_REQUIRES_SPLIT', // every parallel join must have a reachable parallel split upstream
  INSUFFICIENT_MERGE_STEPS = 'INSUFFICIENT_MERGE_STEPS', // number of merge steps must be >= number of parallel split steps

  // ID RULES

  STEP_IDS_UNIQUE = 'STEP_IDS_UNIQUE', // unique stepId
  TRANSITION_IDS_UNIQUE = 'TRANSITION_IDS_UNIQUE', // unique transitionId
  STEP_GROUP_IDS_UNIQUE = 'STEP_GROUP_IDS_UNIQUE', // unique stepGroupId

  // SCHEMA RULES

  OUTPUT_SCHEMA_VALID = 'OUTPUT_SCHEMA_VALID', // outputSchema passes AJV validation
  CTX_KEYS_EXIST = 'CTX_KEYS_EXIST', // all referenced ctx keys exist
  CTX_KEYS_MATCH_STEP_OUTPUT_SCHEMA = 'CTX_KEYS_MATCH_STEP_OUTPUT_SCHEMA', // ctx keys must be compatible with step output schema

  // EXPRESSION RULES

  FIELDREF_DOT_PATH_VALID = 'FIELDREF_DOT_PATH_VALID', // FieldRef uses valid dot-notation path
  FIELDREF_STEP_REF_EXISTS = 'FIELDREF_STEP_REF_EXISTS', // step.<id> references an existing step
  FIELDREF_ALLOWED_ROOTS = 'FIELDREF_ALLOWED_ROOTS', // only ctx and step roots allowed in conditions/params
  FIELDREF_CTX_PROJECTED = 'FIELDREF_CTX_PROJECTED', // ctx paths used must be present in contextProjection

  // DATASOURCE RULES

  DATASOURCE_EXISTS_WITH_SCHEMAS = 'DATASOURCE_EXISTS_WITH_SCHEMAS', // datasource exists and has required schemas
  DATASOURCE_ALIAS_UNIQUE_PER_STEP = 'DATASOURCE_ALIAS_UNIQUE_PER_STEP',
  DATASOURCE_ALIAS_NO_SPACES = 'DATASOURCE_ALIAS_NO_SPACES',

  // GROUP RULES

  NO_GROUP_FOR_SPECIAL_NODES = 'NO_GROUP_FOR_SPECIAL_NODES', // start/end/gateways cannot be grouped
  GROUP_STEP_IDS_EXISTS = 'GROUP_STEP_IDS_EXISTS', // stepIds in stepGroup must reference existing steps
  STEP_BELONGS_TO_ONLY_ONE_GROUP = 'STEP_BELONGS_TO_ONLY_ONE_GROUP', // step ID belongs to only one group
  STEP_GROUP_CANNOT_BE_EMPTY = 'STEP_GROUP_CANNOT_BE_EMPTY', // step group cannot be empty
  LEGACY_SERVICE_REQUEST_REQUIRES_STEP_GROUPS = 'LEGACY_SERVICE_REQUEST_REQUIRES_STEP_GROUPS', // at least one stepGroup should be present

  // LEGACY RULES
  LEGACY_SERVICE_REQUEST_TYPE_GROUP_TASK_UNIQUE = 'LEGACY_SERVICE_REQUEST_TYPE_GROUP_TASK_UNIQUE', // (group, task) tuple unique
  LEGACY_SERVICE_REQUEST_TASK_GROUP_EXISTS_IN_TEMPLATE = 'LEGACY_SERVICE_REQUEST_TASK_GROUP_EXISTS_IN_TEMPLATE', // stepGroups with legacyServiceRequestTaskGroupKey must exist in serviceRequestTemplate
  LEGACY_SERVICE_REQUEST_TASK_EXISTS_IN_TEMPLATE = 'LEGACY_SERVICE_REQUEST_TASK_EXISTS_IN_TEMPLATE', // steps with legacyServiceRequestTaskKey must exist in taskGroup template
  LEGACY_SERVICE_REQUEST_TASK_ONLY_ON_HUMAN_STEP = 'LEGACY_SERVICE_REQUEST_TASK_ONLY_ON_HUMAN_STEP', // only human steps can have legacyServiceRequestTask mapping

  // FORM TEMPLATE RULES
  HUMAN_FORM_TEMPLATE_EXISTS = 'HUMAN_FORM_TEMPLATE_EXISTS', // human.form step must reference an existing form template
  HUMAN_FORM_TEMPLATE_STATUS_VALID = 'HUMAN_FORM_TEMPLATE_STATUS_VALID', // human.form step must reference a form template with COMPLETED or LIVE status

  // HUMAN STEP RULES
  HUMAN_STEP_ESTIMATED_TIME_REQUIRED = 'HUMAN_STEP_ESTIMATED_TIME_REQUIRED', // human steps must have estimatedTimeToComplete

  // LLM STEP RULES
  LLM_CONFIG_REQUIRED = 'LLM_CONFIG_REQUIRED', // sys.llm step must have an llm config object
  LLM_PROVIDER_REQUIRED = 'LLM_PROVIDER_REQUIRED', // llm.provider must be a non-empty string
  LLM_MODEL_ID_REQUIRED = 'LLM_MODEL_ID_REQUIRED', // llm.modelId must be a non-empty string
  LLM_USER_PROMPT_REQUIRED = 'LLM_USER_PROMPT_REQUIRED', // llm.userPrompt must be present

  // STRUCTURE RULES
  STRUCTURE_INVALID = 'STRUCTURE_INVALID', // workflow definition structure does not match DTO

  // REDIRECT STEP RULES
  REDIRECT_STEP_URL_MISSING = 'REDIRECT_STEP_URL_MISSING', // human.redirect step must have a redirectUrl
  REDIRECT_STEP_URL_INVALID = 'REDIRECT_STEP_URL_INVALID', // human.redirect step redirectUrl must be a valid relative path or https:// URL
  REDIRECT_STEP_MISSING_BINDING = 'REDIRECT_STEP_MISSING_BINDING', // human.redirect step has template variables without valueMapping entries

  // TRIGGER RULES
  TRIGGER_IDS_UNIQUE = 'TRIGGER_IDS_UNIQUE', // trigger IDs must be unique
  TRIGGER_EVENT_TYPE_REQUIRED = 'TRIGGER_EVENT_TYPE_REQUIRED', // internal.event triggers must have eventType
  TRIGGER_EVENT_TYPE_VALID = 'TRIGGER_EVENT_TYPE_VALID', // eventType must exist in the event catalog

  // CONTEXT PROJECTION RULES
  CONTEXT_PROJECTION_DUPLICATE_TARGET = 'CONTEXT_PROJECTION_DUPLICATE_TARGET', // same targetPath in multiple projections (non-append mode)
  CONTEXT_PROJECTION_FIELD_NOT_IN_FORM = 'CONTEXT_PROJECTION_FIELD_NOT_IN_FORM', // projection references non-existent form field
  CONTEXT_PROJECTION_DYNAMIC_FIELD_UNVALIDATED = 'CONTEXT_PROJECTION_DYNAMIC_FIELD_UNVALIDATED', // projection references dynamic field that cannot be validated at publish time
  FORM_CONTEXT_NOT_AVAILABLE = 'FORM_CONTEXT_NOT_AVAILABLE', // form requires context not available at step

  // START STEP RULES
  START_STEP_INCOMING_ONLY_FROM_TRIGGERS = 'START_STEP_INCOMING_ONLY_FROM_TRIGGERS', // START step can only receive connections from triggers

  // TRIGGER ERP RULES
  TRIGGER_ERP_PROVIDER_REQUIRED = 'TRIGGER_ERP_PROVIDER_REQUIRED', // ERP triggers must have a provider

  // API CALL STEP RULES
  API_CALL_METHOD_REQUIRED = 'API_CALL_METHOD_REQUIRED', // sys.api_call step must have an HTTP method
  API_CALL_URL_REQUIRED = 'API_CALL_URL_REQUIRED', // sys.api_call step must have a URL configuration
  API_CALL_URL_PATH_REQUIRED = 'API_CALL_URL_PATH_REQUIRED', // internal API call must have a path
  API_CALL_URL_VARIABLE_BINDING_MISSING = 'API_CALL_URL_VARIABLE_BINDING_MISSING', // URL contains {{varName}} but no binding provided
  API_CALL_RAW_BODY_INVALID_JSON = 'API_CALL_RAW_BODY_INVALID_JSON', // raw body content is not valid JSON
}

export enum WorkflowValidationErrorCode {
  GRAPH_INVALID = 'GRAPH_INVALID',
  ID_INVALID = 'ID_INVALID',
  SCHEMA_INVALID = 'SCHEMA_INVALID',
  EXPRESSION_INVALID = 'EXPRESSION_INVALID',
  DATASOURCE_INVALID = 'DATASOURCE_INVALID',
  GROUPING_INVALID = 'GROUPING_INVALID',
  LEGACY_CONFIG_INVALID = 'LEGACY_CONFIG_INVALID',
  REDIRECT_CONFIG_INVALID = 'REDIRECT_CONFIG_INVALID',
  LLM_CONFIG_INVALID = 'LLM_CONFIG_INVALID',
  TRIGGER_INVALID = 'TRIGGER_INVALID',
  CONTEXT_PROJECTION_INVALID = 'CONTEXT_PROJECTION_INVALID',
  API_CALL_CONFIG_INVALID = 'API_CALL_CONFIG_INVALID',
}
